import { useCallback, useState } from "react";
import { API_BASE_URL } from "../constants/api";
import {
  EVENT_ACTION_PP_CHANGE_VERSION,
  EVENT_CATEGORY_PRIVACY_POLICY,
} from "../constants/analytics";
import { GPPJsonConfig, VersionHistoryState } from "../types/version-history";
import Http from "../services/http";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import {
  buildPPSourcePath,
  buildVersionHistoryPath,
  hasPrivacyPolicyForLanguage,
  parseLanguageString,
} from "../utils/privacyPolicyUtils";

// Module-level cache for version history responses to avoid re-fetching on tab switches.
// Placed at module level so it persists across component re-mounts (e.g., React.StrictMode)
const versionHistoryCache = new Map<string, GPPJsonConfig>();

/**
 * Clear the version history cache (useful for testing)
 */
export const clearVersionHistoryCache = () => versionHistoryCache.clear();

interface UsePrivacyPolicyVersionHistoryReturn {
  versionHistories: VersionHistoryState[];
  hasVersionHistory: boolean;
  selectedHistory: VersionHistoryState | undefined;
  ppSource_Global: string;
  ppSource_Local: string;
  handleSelectHistory: (history: VersionHistoryState) => Promise<void>;
  loadPrivacyPolicyVersionHistory: (
    countryCode: string,
    langCode: string
  ) => Promise<void>;
}

/**
 * Custom hook for managing privacy policy version history
 * Handles version history loading, selection, and source file management
 */
export const usePrivacyPolicyVersionHistory = (
  language: string,
  hasLPP: boolean
): UsePrivacyPolicyVersionHistoryReturn => {
  const [ppSource_Global, setPpSource_Global] = useState(
    `${API_BASE_URL}/privacy_policy/en.html`
  );
  const [ppSource_Local, setPpSource_Local] = useState(
    `${API_BASE_URL}/privacy_policy/en.html`
  );
  const [hasVersionHistory, setHasVersionHistory] = useState(false);
  const [versionHistories, setVersionHistories] = useState<
    VersionHistoryState[]
  >([]);
  const [selectedHistory, setSelectedHistory] = useState<VersionHistoryState>();

  /**
   * Set the privacy policy source files based on date and language
   * For LPP countries, builds both global and local paths
   * Each can have different version dates
   */
  const setPPsourceFiles = useCallback(
    (globalDate: string, localDate: string, lang: string) => {
      const [langCode, countryCode] = parseLanguageString(lang);
      const ppExist = hasPrivacyPolicyForLanguage(lang);

      const globalPath = buildPPSourcePath(
        countryCode,
        "global",
        langCode,
        globalDate,
        ppExist,
        hasLPP
      );
      setPpSource_Global(globalPath);

      if (hasLPP) {
        const localPath = buildPPSourcePath(
          countryCode,
          "local",
          langCode,
          localDate,
          ppExist,
          hasLPP
        );
        setPpSource_Local(localPath);
      }
    },
    [hasLPP]
  );

  /**
   * Load version history configuration from the server
   * For LPP countries, fetches both global and local version histories in parallel
   */
  const loadPrivacyPolicyVersionHistory = useCallback(
    async (countryCode: string, langCode: string) => {
      try {
        // Fetch global version history
        const globalUrl = buildVersionHistoryPath(
          countryCode,
          "global",
          langCode,
          hasLPP
        );

        const cachedGlobalResponse = versionHistoryCache.get(globalUrl);
        const globalResponse: GPPJsonConfig =
          cachedGlobalResponse || (await Http.get(globalUrl));

        if (!cachedGlobalResponse) {
          versionHistoryCache.set(globalUrl, globalResponse);
        }

        // Fetch local version history if applicable
        let localResponse: GPPJsonConfig | undefined;
        if (hasLPP) {
          const localUrl = buildVersionHistoryPath(
            countryCode,
            "local",
            langCode,
            hasLPP
          );

          const cachedLocalResponse = versionHistoryCache.get(localUrl);
          if (cachedLocalResponse) {
            localResponse = cachedLocalResponse;
          } else {
            const fetched: GPPJsonConfig = await Http.get(localUrl);
            localResponse = fetched;
            versionHistoryCache.set(localUrl, fetched);
          }
        }

        // Use global response as source of truth for version list
        const response = globalResponse;

        // Filter out draft versions and sort by display date descending
        const publishedVersions = response.versions
          .filter((version) => version.status !== "DRAFT")
          .sort(
            (a, b) =>
              new Date(b.displayDate).getTime() -
              new Date(a.displayDate).getTime()
          );

        // Determine if version history should be shown
        const shouldShowVersionHistory =
          response.enabled && publishedVersions.length > 1;

        setHasVersionHistory(shouldShowVersionHistory);

        if (shouldShowVersionHistory) {
          setVersionHistories(publishedVersions);

          // Set default selected history
          const defaultHistory =
            publishedVersions.find(
              (version) => version.name === response.current
            ) || publishedVersions[0];

          setSelectedHistory(defaultHistory);
        } else {
          setVersionHistories([]);
          setSelectedHistory(undefined);
        }

        // Set source files - use separate current versions for global and local
        if (response.current) {
          const localCurrent = localResponse?.current || response.current;
          setPPsourceFiles(response.current, localCurrent, language);
        } else {
          // Fallback to global en.html
          const globalGPPURL = `${API_BASE_URL}/privacy_policy/ZZ/en/version_history.json`;
          const gppVersionHistoryResponse: GPPJsonConfig =
            await Http.get(globalGPPURL);
          setPpSource_Global(
            `${API_BASE_URL}/privacy_policy/ZZ/en/${gppVersionHistoryResponse.current}.html`
          );
          if (hasLPP) {
            setPpSource_Local(
              `${API_BASE_URL}/privacy_policy/ZZ/en/${gppVersionHistoryResponse.current}.html`
            );
          }
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        const globalUrl = buildVersionHistoryPath(
          countryCode,
          "global",
          langCode,
          hasLPP
        );
        const localUrl = hasLPP
          ? buildVersionHistoryPath(countryCode, "local", langCode, hasLPP)
          : "";

        // Console info
        console.info(
          "No current version found; falling back to global HTML",
          JSON.stringify({
            error: errorMessage,
            globalSourcePath: globalUrl,
            localSourcePath: localUrl || "N/A",
            language,
            langCode,
            countryCode,
            hasLPP,
            fallbackURL: `${API_BASE_URL}/privacy_policy/ZZ/global/en/07012024.html`,
          })
        );
        setHasVersionHistory(false);
        setVersionHistories([]);
        setSelectedHistory(undefined);
        setPpSource_Global(
          `${API_BASE_URL}/privacy_policy/ZZ/global/en/07012024.html`
        );
        if (hasLPP) {
          setPpSource_Local(
            `${API_BASE_URL}/privacy_policy/ZZ/local/en/07012024.html`
          );
        }
      }
    },
    [language, setPPsourceFiles, hasLPP]
  );

  /**
   * Handle version history selection - applies to both global and local
   */
  const handleSelectHistory = useCallback(
    async (history: VersionHistoryState) => {
      if (hasVersionHistory) {
        setSelectedHistory(history);
        // When user selects a version, use it for both global and local
        setPPsourceFiles(history.name, history.name, language);
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_PRIVACY_POLICY,
          EVENT_ACTION_PP_CHANGE_VERSION
        );
      }
    },
    [hasVersionHistory, language, setPPsourceFiles]
  );

  return {
    versionHistories,
    hasVersionHistory,
    selectedHistory,
    ppSource_Global,
    ppSource_Local,
    handleSelectHistory,
    loadPrivacyPolicyVersionHistory,
  };
};
